/**
 * forecastLinkOwners.ts — Forecast programme F4b (register row 81): the Link
 * owners modal's MODEL, pure, from ONE /v1/sales/forecast response.
 * LinkOwnersModal.tsx renders exactly what this returns.
 *
 * Spec §3.4 ("Link owners ›") under ADOPTION §18.7 and the design note §38.4
 * shape 1 (RULED §38.7 item 1):
 *
 * - THE LIST IS THE WIRE'S: `unattributedWon.items` — the won deals of the
 *   period with no owner link, the same population as the Attribution card's
 *   unlinked half. Nothing is re-derived from deals here.
 * - A PROPOSAL IS THE SERVER'S, and there is ONE rule: the deal's legacy owner
 *   text equals an employee's full name exactly (after normalisation). The
 *   spec says "pre-matched by name similarity"; that was RULED OUT — no score,
 *   no fuzzy match. A row is pre-selected only when the wire carries a
 *   proposal; this file never guesses one.
 * - A deal KEPT UNLINKED is listed as kept — never hidden — and carries no
 *   proposal. It can still be linked, which resets the mark.
 * - Linking is the deal's own PUT (`assignedEmployeeId`), one deal per
 *   request; "Keep unlinked" is the same PUT with `keepOwnerUnlinked`. There
 *   is no bulk write.
 * - A pre-F4b response (no `unattributedWon`) or an empty list yields null:
 *   the card renders no link rather than an empty dialog.
 */
import type { SalesForecast, SalesForecastUnattributedDeal } from '../../../../api/types'
import { money } from '../../../../lib/money'
import { periodName } from './forecastHero'

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const NO_OWNER_NAME = 'No owner name on the deal'
export const UNCAPTURED_VALUE = 'Non-GBP, no captured rate'

export interface LinkOwnerRow {
  dealId: string
  title: string
  /** "£1,000,000.00", or UNCAPTURED_VALUE for a deal the fence dropped. */
  valueText: string
  /** `Owner on the deal: “Klemen”`, or NO_OWNER_NAME. */
  ownerLine: string
  kept: boolean
  /** "Kept unlinked since 2 Oct 2026" — present only when kept. */
  keptLine: string | null
  /** The server's proposal — never present on a kept deal. */
  proposal: { employeeId: string; employeeName: string } | null
  /** "Name matches Pat Pensioner exactly" — present only with a proposal. */
  proposalLine: string | null
}

export interface LinkOwnersModel {
  period: string
  rows: LinkOwnerRow[]
  /** What the list is, in a sentence. */
  summary: string
  /** What the matching did, in a sentence — including when it did nothing. */
  matching: string
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** The mark's date, in UTC — the server stamped it; the client clock is not read. */
function keptSince(iso: string): string {
  const d = new Date(iso)
  return `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

export function linkOwnerRow(d: SalesForecastUnattributedDeal): LinkOwnerRow {
  const kept = !!d.keptUnlinkedAt
  const proposal = !kept && d.proposal ? { employeeId: d.proposal.employeeId, employeeName: d.proposal.employeeName } : null
  return {
    dealId: d.dealId,
    title: d.title,
    valueText: d.pence === null ? UNCAPTURED_VALUE : money(d.pence / 100),
    ownerLine: d.ownerText.trim() === '' ? NO_OWNER_NAME : `Owner on the deal: “${d.ownerText}”`,
    kept,
    keptLine: kept ? `Kept unlinked since ${keptSince(d.keptUnlinkedAt!)}` : null,
    proposal,
    proposalLine: proposal ? `Name matches ${proposal.employeeName} exactly` : null,
  }
}

/** The model, or null when there is nothing to link (or the wire predates F4b). */
export function linkOwnersModel(f: SalesForecast): LinkOwnersModel | null {
  const items = f.unattributedWon?.items
  if (!items || items.length === 0) return null
  const period = periodName(f.periodKind, f.periodStart)
  const rows = items.map(linkOwnerRow)
  const n = rows.length
  const keptCount = rows.filter(r => r.kept).length
  const open = n - keptCount
  const matched = rows.filter(r => r.proposal).length
  const summary = `${n} won ${plural(n, 'deal', 'deals')} in ${period} ${plural(n, 'has', 'have')} no owner link, so ${plural(n, 'its', 'their')} value counts toward nobody's target.`
    + (keptCount > 0 ? ` ${keptCount} ${plural(keptCount, 'is', 'are')} kept unlinked on purpose.` : '')
  let matching: string
  if (open === 0) matching = 'Every deal here is kept unlinked, so no owner is suggested.'
  else if (matched === 0) matching = 'No owner name on these deals matches an employee exactly, so nothing is pre-selected.'
  else matching = `${matched} of ${open} ${plural(matched, 'carries', 'carry')} an owner name that matches an employee exactly, and ${plural(matched, 'is', 'are')} pre-selected.`
  return { period, rows, summary, matching }
}
